import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  subscribeToAuthChanges, 
  signInWithEmail, 
  signInWithGoogle, 
  logout,
  getUserRoles
} from '../services/auth';
import { auth } from '../firebase/config';
import { createUserWithEmailAndPassword } from 'firebase/auth';

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState('');
  const [partnerName, setPartnerName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = subscribeToAuthChanges(async (user) => {
      
      if (user) {
        setCurrentUser(user);
        // Fetch user roles when user is authenticated
        try {
          const role = await getUserRoles();
          setUserRole(role.role);
          setPartnerName(role.partner_name);
        } catch (error) {
          console.error('Error fetching user roles:', error);
          setUserRole('');
          setPartnerName('');
        }
        navigate('/dashboard');
      } else {
        setUserRole('');
        setPartnerName('');
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, [navigate]);

  const login = async (email, password) => {
    setError('');
    try {
      await signInWithEmail(email, password);
      navigate('/dashboard');
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const googleLogin = async () => {
    setError('');
    try {
      const user = await signInWithGoogle();
      console.log('User after Google sign-in:', user); // Log the user object
      if (user) {
        navigate('/dashboard');
      } else {
        setError('Google sign-in failed');
        return;
      }
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const signOut = async () => {
    setError('');
    try {
      await logout();
      // Reset all state
      setCurrentUser(null);
      setUserRole('');
      setPartnerName('');
      // Redirect to login page
      navigate('/');
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const registerUser = async (email, password) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      // Add any additional user data handling here
      return userCredential.user;
    } catch (error) {
      throw error;
    }
  };

  // Function to check if user has a specific role
  const hasRole = (role) => {
    return userRole === role;
  };

  const fetchPartnerName = () => {
    return partnerName ;
  };

  const value = {
    currentUser,
    userRole,
    partnerName,
    hasRole,
    fetchPartnerName,
    login,
    googleLogin,
    signOut,
    error,
    registerUser
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};